import React, { forwardRef } from 'react';
import { observer } from 'mobx-react-lite';

import TooltipTrigger from 'src/components/common/tooltip-trigger';
import Checkbox from 'src/components/ui/checkbox';
import Input from 'src/components/ui/input';
import InputPhone from 'src/components/ui/input-phone';
import { ReactComponent as HintIcon } from 'src/assets/icons/hint.svg';

import { CountryCode, InputMaxLength, IS_MESHULAM_PAYMENT, LOCALES } from 'src/constants';
import type { PurchaserFormProps } from './purchaser-form.props';

import './purchaser-form.scss';

const PurchaserForm = observer(
  forwardRef<HTMLDivElement, PurchaserFormProps>(
    ({ formFields, isAgreementChecked, onChange, toggleAgreement }, ref) => {
      const { countryCode, firstName, lastName, phone, email, message } = formFields;

      const handleChangeCountryCode = React.useMemo(() => onChange('countryCode'), [onChange]);
      const handleChangePhone = React.useMemo(() => onChange('phone'), [onChange]);

      return (
        <div className="purchaser-form" ref={ref}>
          <h2 className="purchaser-form__title">{LOCALES.PURCHASER}</h2>
          <form className="purchaser-form__form">
            <div className="purchaser-form__form-wrapper">
              <Input
                id="first-name"
                className="purchaser-form__input"
                isLabelWithAsterisk
                label={LOCALES.FIRST_NAME}
                isValid={firstName.isValid}
                maxLength={InputMaxLength.TEXT}
                onChange={onChange('firstName')}
                textError={firstName.textError}
                value={firstName.value}
              />
              <Input
                id="last-name"
                className="purchaser-form__input"
                isLabelWithAsterisk
                label={LOCALES.LAST_NAME}
                isValid={lastName.isValid}
                maxLength={InputMaxLength.TEXT}
                onChange={onChange('lastName')}
                textError={lastName.textError}
                value={lastName.value}
              />
              <InputPhone
                id="phone"
                className="purchaser-form__input"
                countryCode={countryCode.value as CountryCode}
                isLabelWithAsterisk
                isValid={phone.isValid}
                label={LOCALES.PHONE_NUMBER}
                onChange={handleChangePhone}
                onChangeCountryCode={handleChangeCountryCode}
                textError={phone.textError}
                value={phone.value}
              />
              <Input
                id="email"
                className="purchaser-form__input purchaser-form__input_email"
                icon={
                  <TooltipTrigger text={LOCALES.RECEIVE_REPORT_BY_EMAIL}>
                    <HintIcon />
                  </TooltipTrigger>
                }
                inputMode="email"
                isLabelWithAsterisk
                isPlaceholderOnFocus
                isValid={email.isValid}
                label={LOCALES.EMAIL}
                maxLength={InputMaxLength.EMAIL}
                onChange={onChange('email')}
                placeholder="user@example.com"
                textError={email.textError}
                value={email.value}
              />
              <Input
                className="purchaser-form__textarea"
                hasCounter
                id="message"
                label={LOCALES.SPECIAL_INSTRUCTIONS}
                maxLength={InputMaxLength.TEXT}
                onChange={onChange('message')}
                rows={3}
                value={message.value}
              />
            </div>

            <div className="purchaser-form__reference">* - {LOCALES.REQUIRED_DATA}</div>

            {IS_MESHULAM_PAYMENT && (
              <Checkbox
                id="termsAgreedCheckbox"
                isChecked={isAgreementChecked}
                label={LOCALES.TERMS_AGREED_TEXT}
                linkLabel={LOCALES.TERMS_AGREED_LINK}
                linkUrl={window.PRIVACY_POLICY_URL}
                onChange={toggleAgreement}
              />
            )}
          </form>
        </div>
      );
    }
  )
);

export default PurchaserForm;
